import React, { useState } from 'react';

function ElasticityTable({ data, onAddToWatchlist, watchedStocks }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterTicker, setFilterTicker] = useState('all');
  const [filterExpiration, setFilterExpiration] = useState('all');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    return `${num.toFixed(1)}%`;
  };

  const formatMultiplier = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    if (num <= 0) return '--';
    return `${num.toFixed(1)}x`;
  };

  // Get background color for multiplier (heatmap effect)
  const getMultiplierColor = (value) => {
    if (value == null || isNaN(value) || value <= 0) return {};
    const num = Number(value);

    // Color scale: low (gray) -> medium (green) -> high (bright green)
    if (num < 2) return { backgroundColor: 'rgba(34, 197, 94, 0.1)' };
    if (num < 4) return { backgroundColor: 'rgba(34, 197, 94, 0.2)' };
    if (num < 6) return { backgroundColor: 'rgba(34, 197, 94, 0.3)' };
    if (num < 10) return { backgroundColor: 'rgba(34, 197, 94, 0.4)' };
    if (num < 20) return { backgroundColor: 'rgba(34, 197, 94, 0.5)' };
    return { backgroundColor: 'rgba(34, 197, 94, 0.6)' };
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const isWatched = (ticker) => {
    return watchedStocks?.some(stock => stock.ticker === ticker) || false;
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-right py-2 px-2 cursor-pointer hover:opacity-80 whitespace-nowrap"
      style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
      onClick={() => handleSort(field)}
    >
      {label}{' '}
      {sortField === field && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  );

  // Early return if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No elasticity data available</p>
        <p className="text-sm text-gray-500 mt-2">Use the form above to analyze options elasticity</p>
      </div>
    );
  }

  // Get unique tickers and expirations for filtering
  const uniqueTickers = [...new Set(data.map(row => row.Ticker))];
  const uniqueExpirations = [...new Set(data.map(row => row['Exp Label']))];

  // Filter data
  let filteredData = data;
  if (filterTicker !== 'all') {
    filteredData = filteredData.filter(row => row.Ticker === filterTicker);
  }
  if (filterExpiration !== 'all') {
    filteredData = filteredData.filter(row => row['Exp Label'] === filterExpiration);
  }

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;

    const aVal = a[sortField];
    const bVal = b[sortField];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Multiplier columns to display
  const multiplierCols = [
    { key: '10% Mult', label: '10% Move' },
    { key: '20% Mult', label: '20% Move' },
    { key: '35% Mult', label: '35% Move' },
    { key: '50% Mult', label: '50% Move' },
    { key: '100% Mult', label: '100% Move' },
    { key: '200% Mult', label: '200% Move' },
    { key: '1000% Mult', label: '1000% Move' },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Filter by Ticker</label>
          <select
            value={filterTicker}
            onChange={(e) => setFilterTicker(e.target.value)}
            className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Tickers</option>
            {uniqueTickers.map(ticker => (
              <option key={ticker} value={ticker}>{ticker}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Filter by Expiration</label>
          <select
            value={filterExpiration}
            onChange={(e) => setFilterExpiration(e.target.value)}
            className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Expirations</option>
            {uniqueExpirations.map(exp => (
              <option key={exp} value={exp}>{exp}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-max">
          <thead>
            {/* Header Group Row */}
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              <th className="text-left py-2 px-2 sticky left-0 z-10" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }} colSpan="3">
                CONTRACT
              </th>
              <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="4">
                DETAILS
              </th>
              <th className="text-center py-2 px-2" style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }} colSpan="7">
                MULTIPLIERS (Leverage)
              </th>
              <th className="text-center py-2 px-2 sticky right-0 z-10" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                Actions
              </th>
            </tr>

            {/* Column Headers Row */}
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              {/* Contract */}
              <th className="text-left py-2 px-2 sticky left-0 z-10" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                Ticker
              </th>
              <SortableHeader field="Exp Label" label="Expiration" />
              <SortableHeader field="Strike" label="Strike" />

              {/* Details */}
              <SortableHeader field="Strike %" label="Strike %" />
              <SortableHeader field="Current Premium" label="Premium" />
              <SortableHeader field="IV" label="IV" />
              <SortableHeader field="Holding Days" label="Hold Days" />

              {/* Multipliers */}
              {multiplierCols.map(col => (
                <SortableHeader key={col.key} field={col.key} label={col.label} />
              ))}

              {/* Actions */}
              <th className="text-center py-2 px-2 sticky right-0 z-10" style={{ backgroundColor: '#1a1a1a' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index} className="border-b border-gray-800 hover:bg-gray-700/30">
                {/* Contract */}
                <td className="py-2 px-2 font-medium text-white sticky left-0 bg-gray-800">
                  {isWatched(row.Ticker) && <span className="mr-1">&#11088;</span>}
                  {row.Ticker}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  <div className="text-white">{row['Exp Label']}</div>
                  <div className="text-xs text-gray-500">{row.Expiration}</div>
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatCurrency(row.Strike)}
                </td>

                {/* Details */}
                <td className="py-2 px-2 text-right text-white" style={{ borderLeft: '1px solid #333' }}>
                  {row['Strike %']}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {formatCurrency(row['Current Premium'])}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {row.IV ? formatPercent(row.IV) : '--'}
                </td>
                <td className="py-2 px-2 text-right text-white">
                  {row['Holding Days']}
                </td>

                {/* Multipliers */}
                {multiplierCols.map(col => (
                  <td
                    key={col.key}
                    className="py-2 px-2 text-right text-white font-medium"
                    style={{
                      ...getMultiplierColor(row[col.key]),
                      ...(col.key === '10% Mult' ? { borderLeft: '1px solid #333' } : {})
                    }}
                  >
                    {formatMultiplier(row[col.key])}
                  </td>
                ))}

                {/* Actions */}
                <td className="py-2 px-2 text-center sticky right-0 bg-gray-800">
                  {!isWatched(row.Ticker) && onAddToWatchlist && (
                    <button
                      onClick={() => onAddToWatchlist(row.Ticker)}
                      className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400 hover:border-blue-300"
                    >
                      Watch
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="flex justify-between items-center text-sm text-gray-400">
        <div>
          Showing {sortedData.length} of {data.length} contracts
        </div>
        <div className="text-xs">
          <span className="inline-block w-4 h-4 mr-1 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.3)' }}></span>
          Higher multiplier = more leverage
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="bg-gray-700/30 rounded-lg p-4 mt-4">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Reading the Results</h4>
        <ul className="text-xs text-gray-400 space-y-1">
          <li>• A <strong>3.5x multiplier</strong> at 10% move means: if stock goes up 10%, option gains ~35%</li>
          <li>• OTM options show higher multipliers but require larger moves to profit</li>
          <li>• Shorter expirations amplify leverage but have less time for the move to occur</li>
          <li>• Compare multipliers across different expirations to find optimal risk/reward</li>
        </ul>
      </div>
    </div>
  );
}

export default ElasticityTable;
